import { z } from 'zod';
import { RegulatoryIdentitySchema } from '../counterparties/counterparties.schema.js';

import {
  locationKindValues,
  type CreateLocationRequest,
  type CreateLocationResponse,
  type GetLocationRequest,
  type GetLocationResponse,
  type ListLocationsByStoreRequest,
  type ListLocationsByStoreResponse,
  type LocationDto,
  type LocationKind,
  type UpdateLocationRequest,
  type UpdateLocationResponse,
} from './location.dto.js';

const timestampSchema = z.iso.datetime();
const timeZoneSchema = z.string().trim().min(1).refine(isValidIanaTimeZone, { message: 'Invalid time zone' });

function isValidIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

const locationAddressSchema = z.object({
  addressLine1: z.string().trim().min(1),
  addressLine2: z.string().trim().min(1).optional(),
  city: z.string().trim().min(1),
  state: z.string().trim().min(1),
  postalCode: z.string().trim().min(1),
});

export const LocationSchema: z.ZodType<LocationDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    storeId: z.uuid(),
    name: z.string().trim().min(1),
    code: z.string().trim().min(1),
    kind: z.enum(locationKindValues),
    rcNumber: z.string().trim().min(1),
    regulatoryIdentity: RegulatoryIdentitySchema.nullable().optional(),
    regulatoryEntityId: z.uuid().nullable().optional(),
    timezone: timeZoneSchema.optional(),
    isActive: z.boolean(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .extend(locationAddressSchema.shape);

export const CreateLocationRequestSchema: z.ZodType<CreateLocationRequest> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    name: z.string().trim().min(1),
    code: z.string().trim().min(1),
    kind: z.enum(locationKindValues),
    rcNumber: z.string().trim().min(1),
    timezone: timeZoneSchema.optional(),
    isActive: z.boolean(),
  })
  .extend(locationAddressSchema.shape);

export const CreateLocationResponseSchema: z.ZodType<CreateLocationResponse> = z.object({
  location: LocationSchema,
});

export const UpdateLocationRequestSchema: z.ZodType<UpdateLocationRequest> = z
  .object({
    companyId: z.uuid(),
    storeId: z.uuid(),
    locationId: z.uuid(),
    name: z.string().trim().min(1).optional(),
    code: z.string().trim().min(1).optional(),
    kind: z.enum(locationKindValues).optional(),
    rcNumber: z.string().trim().min(1).optional(),
    timezone: timeZoneSchema.nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .extend(locationAddressSchema.partial().shape)
  .refine(
    (value) =>
      value.name !== undefined ||
      value.code !== undefined ||
      value.kind !== undefined ||
      value.rcNumber !== undefined ||
      value.timezone !== undefined ||
      value.isActive !== undefined ||
      value.addressLine1 !== undefined ||
      value.addressLine2 !== undefined ||
      value.city !== undefined ||
      value.state !== undefined ||
      value.postalCode !== undefined,
    { message: 'At least one field must be provided to update location' },
  );

export const UpdateLocationResponseSchema: z.ZodType<UpdateLocationResponse> = z.object({
  location: LocationSchema,
});

export const GetLocationRequestSchema: z.ZodType<GetLocationRequest> = z.object({
  companyId: z.uuid(),
  locationId: z.uuid(),
});

export const GetLocationResponseSchema: z.ZodType<GetLocationResponse> = z.object({
  location: LocationSchema,
});

export const ListLocationsByStoreRequestSchema: z.ZodType<ListLocationsByStoreRequest> = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(200).optional(),
});

export const ListLocationsByStoreResponseSchema: z.ZodType<ListLocationsByStoreResponse> = z.object({
  items: z.array(LocationSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(200),
  total: z.number().int().min(0),
});

export const LocationKindSchema: z.ZodType<LocationKind> = z.enum(locationKindValues);
